import { GUID_PATTERN } from "../utils/security.js";

// Defensive client limits, not claims about Microsoft's service limits.
const MAX_HEADER_CHARS = 8_192;
const MAX_CLAIMS_BYTES = 4_096;
const MAX_POLICY_IDS = 20;

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Only the observed CAPOLIDS request is supported; this is not a generic claims parser. */
export function validatePowerPlatformClaims(claims: string): string | null {
  if (!claims || Buffer.byteLength(claims, "utf8") > MAX_CLAIMS_BYTES) return null;
  try {
    const parsed: unknown = JSON.parse(claims);
    const root = record(parsed);
    const access = record(root?.access_token);
    const policies = record(access?.capolids);
    if (
      !root ||
      Object.keys(root).length !== 1 ||
      !access ||
      Object.keys(access).length !== 1 ||
      !policies ||
      Object.keys(policies).length !== 2 ||
      policies.essential !== true ||
      !Array.isArray(policies.values) ||
      policies.values.length < 1 ||
      policies.values.length > MAX_POLICY_IDS ||
      !policies.values.every((id: unknown) => typeof id === "string" && GUID_PATTERN.test(id))
    )
      return null;
    // This exact shape has no whitespace-bearing strings. Round-trip equality
    // rejects duplicate keys and escaped/ambiguous spellings without rewriting
    // the original JSON sent to MSAL. Ordinary JSON whitespace/order is retained.
    if (claims.replace(/[ \t\r\n]/g, "") !== JSON.stringify(parsed)) return null;
    return claims;
  } catch {
    return null;
  }
}

/**
 * Accept a single Bearer challenge, either the observed sole raw-JSON claims
 * parameter or a well-formed quoted standard-base64 parameter. Never consume
 * authority, client_id, resource, or scopes supplied by the HTTP header.
 */
export function extractPowerPlatformClaims(header: string | string[] | undefined): string | null {
  const values = header === undefined ? [] : Array.isArray(header) ? header : [header];
  if (values.length !== 1) return null; // Multiple challenges are ambiguous.
  const raw = values[0];
  if (!raw || raw.length > MAX_HEADER_CHARS || /[^\x20-\x7e\t]/.test(raw)) return null;
  const observed = /^Bearer[ \t]+claims[ \t]*=[ \t]*"(\{.*\})"[ \t]*$/i.exec(raw.trim());
  if (observed?.[1]) return validatePowerPlatformClaims(observed[1]);

  const scheme = /^Bearer[ \t]+/i.exec(raw.trim());
  if (!scheme) return null;
  const text = raw.trim();
  let cursor = scheme[0].length;
  const parameters = new Map<string, string>();
  const token = /^[!#$%&'*+.^_`|~\w-]+/;
  while (cursor < text.length) {
    while (/[ \t]/.test(text[cursor] ?? "")) cursor++;
    const nameMatch = token.exec(text.slice(cursor));
    if (!nameMatch) return null;
    const name = nameMatch[0].toLowerCase();
    cursor += nameMatch[0].length;
    while (/[ \t]/.test(text[cursor] ?? "")) cursor++;
    if (text[cursor++] !== "=" || parameters.has(name)) return null;
    while (/[ \t]/.test(text[cursor] ?? "")) cursor++;
    let value = "";
    const quoted = text[cursor] === '"';
    if (quoted) {
      cursor++;
      let closed = false;
      while (cursor < text.length) {
        const character = text[cursor++];
        if (character === '"') {
          closed = true;
          break;
        }
        if (character === "\\") {
          if (cursor >= text.length) return null;
          value += text[cursor++];
        } else value += character;
      }
      if (!closed) return null;
    } else {
      const valueMatch = token.exec(text.slice(cursor));
      if (!valueMatch) return null;
      value = valueMatch[0];
      cursor += value.length;
    }
    if (name === "claims" && !quoted) return null;
    parameters.set(name, value);
    while (/[ \t]/.test(text[cursor] ?? "")) cursor++;
    if (cursor === text.length) break;
    if (text[cursor++] !== "," || cursor === text.length) return null;
  }
  if (parameters.has("error") && parameters.get("error") !== "insufficient_claims") return null;
  const encoded = parameters.get("claims");
  if (!encoded || !/^[A-Za-z0-9+/]+={0,2}$/.test(encoded) || encoded.length % 4 !== 0) return null;
  const bytes = Buffer.from(encoded, "base64");
  if (bytes.length > MAX_CLAIMS_BYTES || bytes.toString("base64") !== encoded) return null;
  const claims = bytes.toString("utf8");
  if (!Buffer.from(claims, "utf8").equals(bytes)) return null;
  return validatePowerPlatformClaims(claims);
}

/** Never include an MSAL message, claims payload, token, or arbitrary property. */
export function powerPlatformClaimsFailureMessage(error: unknown): string {
  const message = error instanceof Error ? error.message.slice(0, 8_192) : "";
  const code =
    /\bAADSTS(?:53000|53001|53002|53003|530036|50076|50079|65001|70043|50173|700082)\b/.exec(
      message,
    )?.[0];
  return (
    "Power Platform API silent claims authentication failed" +
    (code ? ` (${code})` : "") +
    ". No interactive sign-in was started and no alternate identity was tried. " +
    "Review the sign-in requirements and Conditional Access result before retrying."
  );
}
